/**
 * The client portal: what a client login sees instead of the agency's
 * workspace. Their agency's brand at the top, the dashboards and reports
 * shared with them, and nothing else. The server enforces all of it; this
 * screen simply has nothing else to show.
 */

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';

import { signOut, type Organisation } from '../api.ts';
import { AudienceContext } from '../reports/audience.ts';
import type { Catalogue } from '../reports/reports-api.ts';
import { portalApi, type Branding, type PortalClient, type SharedDocument } from './portal-api.ts';
import { PortalViewer } from './PortalViewer.tsx';

const KIND_LABEL: Record<string, string> = {
  DASHBOARD: 'Dashboard',
  SLIDE_DECK: 'Slide deck',
  DOCUMENT: 'Document',
};
const updated = new Intl.DateTimeFormat('en-ZA', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

interface Home {
  clients: PortalClient[];
  branding: Branding;
  catalogue: Catalogue;
}

function DocumentList({
  documents,
  onOpen,
}: {
  documents: SharedDocument[] | null;
  onOpen: (id: string) => void;
}): ReactNode {
  if (documents === null) return <p className="pt-note">Loading…</p>;
  if (documents.length === 0) {
    return (
      <div className="pt-empty">
        Nothing has been shared with you yet. Your agency will let you know when your first report
        is ready.
      </div>
    );
  }
  return (
    <ul className="pt-list">
      {documents.map((document) => (
        <li key={document.id}>
          <button type="button" className="pt-card" onClick={() => onOpen(document.id)}>
            <span className="rh-kind">{KIND_LABEL[document.kind]}</span>
            <strong>{document.name}</strong>
            <small>Updated {updated.format(new Date(document.updated_at))}</small>
          </button>
        </li>
      ))}
    </ul>
  );
}

export function PortalApp({ organisation }: { organisation: Organisation }): ReactNode {
  const [home, setHome] = useState<Home | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [documents, setDocuments] = useState<SharedDocument[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    portalApi
      .home(organisation.id)
      .then((data) => {
        setHome(data);
        setClientId(data.clients[0]?.id ?? null);
      })
      .catch(() => setProblem('Your reports could not be loaded. Try again shortly.'));
  }, [organisation.id]);

  useEffect(() => {
    if (!clientId) return;
    setDocuments(null);
    portalApi
      .documents(organisation.id, clientId)
      .then((data) => setDocuments(data.items))
      .catch(() => setProblem('Your reports could not be loaded. Try again shortly.'));
  }, [organisation.id, clientId]);

  const client = home?.clients.find((item) => item.id === clientId);
  const brand = home?.branding;
  const style = brand?.accent
    ? ({ '--accent': brand.accent, '--accent-strong': brand.accent } as CSSProperties)
    : undefined;

  return (
    <AudienceContext.Provider value="client">
      <div className="pt" style={style}>
        <header className="pt-top">
          {brand?.logo ? (
            <img className="pt-logo" src={brand.logo} alt={brand.display_name} />
          ) : (
            <strong className="pt-agency">{brand?.display_name ?? organisation.name}</strong>
          )}
          {home && home.clients.length > 1 ? (
            <select
              aria-label="Business"
              value={clientId ?? ''}
              onChange={(event) => {
                setClientId(event.target.value);
                setOpen(null);
              }}
            >
              {home.clients.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          ) : (
            <span className="pt-client">{client?.name}</span>
          )}
          <span className="pt-spacer" />
          <button
            type="button"
            className="btn small secondary"
            onClick={() => {
              signOut();
              window.location.reload();
            }}
          >
            Sign out
          </button>
        </header>

        {problem && (
          <div className="pt-empty" role="alert">
            {problem}
          </div>
        )}

        {open && home && client && (
          <PortalViewer
            org={organisation.id}
            clientId={client.id}
            clientName={client.name}
            branding={home.branding}
            catalogue={home.catalogue}
            documentId={open}
            onBack={() => setOpen(null)}
          />
        )}

        {!open && !problem && (
          <main className="pt-main">
            <h1>Your reports</h1>
            <p className="pt-note">
              Dashboards and reports {brand?.display_name ?? 'your agency'} has shared with{' '}
              {client?.name ?? 'you'}. Numbers are live.
            </p>
            <DocumentList documents={documents} onOpen={setOpen} />
          </main>
        )}
      </div>
    </AudienceContext.Provider>
  );
}
